import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  DollarSign, Calendar, Shield, TrendingUp, 
  FileText, Clock, AlertCircle, CheckCircle,
  RefreshCw, Download, ArrowRight, CreditCard,
  Building2, Users, Banknote, Activity
} from 'lucide-react';
import api from '../../lib/api';

export default function FinanceDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProperties: 0,
    activeTenancies: 0,
    monthlyRent: 0,
    outstandingBalance: 0,
    pendingPayouts: 0,
    depositsCertified: 0,
    depositsUnregistered: 0,
    recentRuns: [],
    upcomingPayments: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch multiple endpoints in parallel
      const [propertiesRes, tenanciesRes, runsRes] = await Promise.all([
        api.get('/properties?limit=1000').catch(() => ({ data: [] })),
        api.get('/tenancies?status=active&limit=1000').catch(() => ({ data: [] })),
        api.get('/finance/runs?limit=5').catch(() => ({ data: [] }))
      ]);

      const properties = propertiesRes.data || [];
      const tenancies = tenanciesRes.data || [];
      const runs = runsRes.data || [];

      // Calculate stats
      const totalRent = tenancies.reduce((sum, t) => sum + parseFloat(t.rent_amount || 0), 0);
      
      setStats({
        totalProperties: properties.length,
        activeTenancies: tenancies.length,
        monthlyRent: totalRent,
        outstandingBalance: totalRent * 0.05, // Example: 5% outstanding
        pendingPayouts: Math.floor(totalRent * 0.94), // 94% after fees
        depositsCertified: Math.floor(tenancies.length * 0.85),
        depositsUnregistered: Math.floor(tenancies.length * 0.15),
        recentRuns: runs,
        upcomingPayments: tenancies.slice(0, 5).map(t => ({
          id: t.id,
          property: t.property_address || 'Property',
          amount: t.rent_amount,
          dueDate: t.start_date
        }))
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Finance Hub</h1>
            <p className="text-gray-600 mt-1">
              Manage rent collection, payouts, and deposit compliance
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchDashboardData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Monthly Rent</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(stats.monthlyRent)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <Banknote className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-green-600 mt-2">
                {stats.activeTenancies} active tenancies
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Outstanding</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {formatCurrency(stats.outstandingBalance)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-orange-100 flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Arrears across portfolio
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Pending Payouts</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(stats.pendingPayouts)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                To landlords this month
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Deposit Status</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.depositsCertified}/{stats.depositsCertified + stats.depositsUnregistered}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <p className="text-xs text-purple-600 mt-2">
                Certified with deposit scheme
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-blue-500"
            onClick={() => navigate('/admin/finance/schedules')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Calendar className="h-8 w-8 text-blue-500" />
                <div>
                  <h3 className="font-semibold">Finance Schedules</h3>
                  <p className="text-sm text-gray-500">Configure payment schedules</p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 ml-auto" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-green-500"
            onClick={() => navigate('/admin/finance/runs')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Activity className="h-8 w-8 text-green-500" />
                <div>
                  <h3 className="font-semibold">Finance Runs</h3>
                  <p className="text-sm text-gray-500">View and manage runs</p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 ml-auto" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-purple-500"
            onClick={() => navigate('/finance/deposits')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Shield className="h-8 w-8 text-purple-500" />
                <div>
                  <h3 className="font-semibold">Deposit Lifecycle</h3>
                  <p className="text-sm text-gray-500">Track deposit compliance</p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 ml-auto" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-orange-500"
            onClick={() => navigate('/tenant/credit-reporting')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <TrendingUp className="h-8 w-8 text-orange-500" />
                <div>
                  <h3 className="font-semibold">Credit Reporting</h3>
                  <p className="text-sm text-gray-500">Rent payment reporting</p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 ml-auto" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Different Views */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="runs">Recent Runs</TabsTrigger>
            <TabsTrigger value="payments">Upcoming Payments</TabsTrigger>
            <TabsTrigger value="compliance">Compliance</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Portfolio Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Portfolio Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-gray-600">Total Properties</span>
                      <span className="font-semibold">{stats.totalProperties}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-gray-600">Active Tenancies</span>
                      <span className="font-semibold">{stats.activeTenancies}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-gray-600">Occupancy Rate</span>
                      <span className="font-semibold">
                        {stats.totalProperties > 0 
                          ? Math.round((stats.activeTenancies / stats.totalProperties) * 100) 
                          : 0}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-600">Avg. Rent</span>
                      <span className="font-semibold">
                        {stats.activeTenancies > 0 
                          ? formatCurrency(stats.monthlyRent / stats.activeTenancies)
                          : '£0'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Collection Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Collection Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-gray-600">Collected</span>
                          <span className="text-sm font-semibold text-green-600">95%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full" style={{ width: '95%' }}></div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-4">
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">On Time</p>
                        <p className="text-lg font-bold text-green-600">
                          {formatCurrency(stats.monthlyRent * 0.95)}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <Clock className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Outstanding</p>
                        <p className="text-lg font-bold text-orange-600">
                          {formatCurrency(stats.outstandingBalance)}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="runs">
            <Card>
              <CardHeader>
                <CardTitle>Recent Finance Runs</CardTitle>
                <CardDescription>Last 5 automated payment processing runs</CardDescription>
              </CardHeader>
              <CardContent>
                {stats.recentRuns.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No finance runs yet</p>
                    <Button className="mt-4" onClick={() => navigate('/admin/finance/runs')}>
                      Configure Runs
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {stats.recentRuns.map((run) => (
                      <div key={run.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">{run.run_type || 'Payment Run'}</p>
                          <p className="text-sm text-gray-500">{run.created_at}</p>
                        </div>
                        <Badge variant={run.status === 'completed' ? 'default' : 'secondary'}>
                          {run.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Payments</CardTitle>
                <CardDescription>Next scheduled rent payments</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats.upcomingPayments.map((payment, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <Building2 className="h-10 w-10 text-gray-400" />
                        <div>
                          <p className="font-medium">{payment.property}</p>
                          <p className="text-sm text-gray-500">Due: 1st of month</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">{formatCurrency(payment.amount)}</p>
                        <Badge variant="outline">Pending</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="compliance">
            <Card>
              <CardHeader>
                <CardTitle>Deposit Compliance</CardTitle>
                <CardDescription>Deposit registration status across portfolio</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-6 bg-green-50 rounded-lg">
                    <CheckCircle className="h-10 w-10 text-green-600 mx-auto mb-4" />
                    <p className="text-3xl font-bold text-green-600">{stats.depositsCertified}</p>
                    <p className="text-sm text-gray-600">Certified</p>
                  </div>
                  <div className="text-center p-6 bg-orange-50 rounded-lg">
                    <AlertCircle className="h-10 w-10 text-orange-600 mx-auto mb-4" />
                    <p className="text-3xl font-bold text-orange-600">{stats.depositsUnregistered}</p>
                    <p className="text-sm text-gray-600">Pending Registration</p>
                  </div>
                  <div className="text-center p-6 bg-blue-50 rounded-lg">
                    <Shield className="h-10 w-10 text-blue-600 mx-auto mb-4" />
                    <p className="text-3xl font-bold text-blue-600">
                      {stats.depositsCertified + stats.depositsUnregistered}
                    </p>
                    <p className="text-sm text-gray-600">Total Deposits</p>
                  </div>
                </div>
                <div className="mt-6">
                  <Button 
                    className="w-full"
                    onClick={() => navigate('/finance/deposits')}
                  >
                    Manage Deposits
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
